import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import FileItem from './FileItem';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '30px 20px',
  cursor: 'pointer',
  borderWidth: 2,
  borderRadius: 8,
  borderColor: 'var(--color-border)',
  borderStyle: 'dashed',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-text-secondary)',
  outline: 'none',
  transition: 'border .24s ease-in-out, background-color .24s ease-in-out, color .24s ease-in-out',
};

const getActiveStyle = (theme) => ({
  borderColor: theme.palette.primary.main,
  backgroundColor: 'var(--color-main-light)',
  color: theme.palette.text.primary,
});

const getAcceptStyle = (theme) => ({
  borderColor: theme.palette.primary.main,
  backgroundColor: 'var(--color-main-light)',
  color: theme.palette.text.primary,
});

const rejectStyle = {
  borderColor: '#d32f2f',
};

const Dropzone = (props) => {
  const {
    onUpload,
    files,
    type = 'image/jpeg, image/png, application/pdf',
    accept,
    icon,
    multiple = true,
    disabled,
    text,
    hideFilesList,
    onRemove,
    withProgress,
    path,
    suffix,
    onUploadComplete,
    loading,
  } = props;
  const theme = useTheme();
  const options = {
    onDrop: (acceptedFiles) => {
      onUpload(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
    multiple,
    disabled: disabled || loading,
  };

  if (type === 'image') {
    options.accept = 'image/jpeg, image/png';
  }
  if (type === 'pdf') {
    options.accept = 'application/pdf';
  }
  if (type === 'csv') {
    options.accept = '.csv';
  }
  if (type === 'archive') {
    options.accept = ".zip,.rar,.7z,.gz";
  }
  if (type === 'zip') {
    options.accept = ".zip";
  }

  if (accept) {
    options.accept = accept;
  }


  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone(options);

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? getActiveStyle(theme) : {}),
      ...(isDragAccept ? getAcceptStyle(theme) : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept, theme]
  );

  const renderText = () => {
    switch (type) {
      case 'image' :
        return 'Drag and drop some images here or click to upload';
      case 'pdf':
        return 'Drag and drop some PDFs here or click to upload';
      case 'csv':
        return 'Drag and drop some .csv files here or click to upload';
      case 'archive':
        return 'Upload scans here. Drag and drop .zip, .rar, or .7z files, or click to select.';
      case 'zip':
        return 'Drag and drop .zip archive here or click to upload';
      default:
        return 'Drag and drop some files here or click to upload';
    }
  };

  return (
    <div className="container">
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        {
          loading ? (
            <CircularProgress size={24} />
          ) : (
            <div>{text || renderText()}</div>
          )
        }
      </div>
      {!hideFilesList && (
        <aside>
          {files.map((file) => (
            <FileItem
              key={file.name}
              file={file}
              withProgress={withProgress}
              onRemove={onRemove}
              icon={icon}
              suffix={suffix}
              path={path}
              onUploadComplete={onUploadComplete}
            />
          ))}
        </aside>
      )}
    </div>
  );
};

Dropzone.propTypes = {
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
  onUploadComplete: PropTypes.func,
  files: PropTypes.array,
  type: PropTypes.oneOf(['image', 'pdf', 'csv', 'archive', 'zip']),
  accept: PropTypes.string,
  icon: PropTypes.oneOf([
    'x-ray',
    'scan',
    'preop',
    'block',
    'image',
    'aoa',
    'usage',
    'graft',
    'coc',
  ]),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  hideFilesList: PropTypes.bool,
  withProgress: PropTypes.bool,
  text: PropTypes.string,
  path: PropTypes.string,
  suffix: PropTypes.string,
};

export default Dropzone;
